import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreatingOrderActions } from 'store/actions';
import { CreatingOrderSelectors } from 'store/selectors';
import { OrderItemStringType } from 'store/features/creatingOrder/types';
import OrderItems from 'components/creatingOrder/OrderItems';
import { RootState } from 'store/rootReducer';

interface IProps {
  itemType: OrderItemStringType;
  disabled: boolean;
  maxAmountReached?: boolean;
}

const OrderItemsContainer: React.FC<React.PropsWithChildren<IProps>> = ({
  itemType,
  disabled,
  maxAmountReached = false,
}) => {
  const dispatch = useDispatch();
  const currency = useSelector(CreatingOrderSelectors.getDefaultCurrency);
  const orderEntities: any[] = useSelector((state: RootState) =>
    CreatingOrderSelectors.getOrderEntities(state, itemType),
  );
  const orderItemsCounts = useSelector((state: RootState) =>
    CreatingOrderSelectors.getOrderItemsCounts(state, itemType),
  );

  const handleAddOrderItem = useCallback(
    (id: number, order: number, hasQuestions = true, count = 1) => {
      dispatch(CreatingOrderActions.addOrderItem(id, itemType, count, hasQuestions, order));
    },
    [dispatch, itemType],
  );

  const handleRemoveOrderItem = useCallback(
    (id: number) => {
      dispatch(CreatingOrderActions.removeOrderItem(id, itemType));
    },
    [dispatch, itemType],
  );

  useEffect(() => {
    if (!disabled)
      orderEntities.forEach((orderEntity, index) => {
        if (
          !orderEntity.sold_out &&
          itemType === 'ticket' &&
          (orderEntity.min_amount_per_order || orderEntities.length === 1) &&
          !orderItemsCounts[orderEntity.id]
        )
          handleAddOrderItem(
            orderEntity.id,
            index,
            orderEntity.questions.length > 0,
            orderEntity.min_amount_per_order || 1,
          );
      });
  }, [orderEntities, itemType, disabled, handleAddOrderItem, orderItemsCounts]);

  return (
    <OrderItems
      itemType={itemType}
      currency={currency}
      disabled={disabled}
      orderEntities={orderEntities.sort((a: any, b: any) => a.sort_order - b.sort_order)}
      orderItemsCounts={orderItemsCounts}
      onAddOrderItem={handleAddOrderItem}
      onRemoveOrderItem={handleRemoveOrderItem}
      maxAmountReached={maxAmountReached}
    />
  );
};

export default memo(OrderItemsContainer);
